import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { leadershipConfig } from "../config/leadershipConfig";
import globalStyles from "../../../global";

const SomaliaLeadership = ({ countryCode = "uae" }) => {
  const { t, i18n } = useTranslation();
  const config = leadershipConfig[countryCode] || leadershipConfig.uae;
  const isRTL = i18n.language === "ar";

  const styles = {
    mainContainer: {
      width: "100%",
      bgcolor: "white",
    },
    wrapper: {
      maxWidth: "1200px",
      mx: "auto",
      py: 12,
      px: { xs: 2, md: 0 },
    },
    contentContainer: {
      display: "flex",
      flexDirection: {
        xs: "column",
        sm: "column",
        md: isRTL ? "row-reverse" : "row",
      },
      justifyContent: "space-between",
      gap: 6,
    },
    textSection: {
      width: "100%",
      flex: { md: "0 0 50%" },
      textAlign: isRTL ? "right" : "left",
    },
    heading: {
      fontFamily: globalStyles.fonts.primaryUAE,
      fontWeight: 600,
      fontSize: {
        md: "40px",
        sm: "28px",
        xs: "24px",
      },
      color: globalStyles.colors.text_green,
    },
    subHeading: {
      fontFamily: globalStyles.fonts.primary,
      my: 2,
      fontWeight: 600,
      fontSize: {
        md: "28px",
        sm: "24px",
        xs: "20px",
      },
      color: globalStyles.colors.uclean_medium_gray,
    },
    description: {
      fontFamily: globalStyles.fonts.primary,
      fontSize: {
        sm: "20px",
        xs: "16px",
      },
      color: globalStyles.colors.medium_gray,
      lineHeight: "auto",
      letterSpacing: "1%",
      mb: 3,
    },
    button: {
      color: globalStyles.colors.uclean_green,
      border: `1px solid ${globalStyles.colors.uclean_green}`,
      borderRadius: "10px",
      px: 6,
      py: 0.75,
      mt: 2,
      textTransform: "none",
      fontFamily: globalStyles.fonts.primary,
      fontSize: {
        sm: "18px",
        xs: "14px",
      },
      fontWeight: 600,
      height: "fit-content",
      "&:hover": {
        backgroundColor: globalStyles.colors.uclean_green,
        color: globalStyles.colors.white,
      },
    },
    videoSection: {
      width: "100%",
      flex: { md: "0 0 50%" },
      textAlign: "center",
    },
    videoContainer: {
      borderRadius: 2,
      width: "100%",
    },
    iframe: {
      width: "100%",
      height: {
        xs: "300px",
        sm: "400px",
        md: "500px",
      },
      border: 0,
    },
  };

  // Dynamically construct translation key for leadership block based on countryCode
  // Example: leadershipUAE, leadershipSRI, etc.
  const leadershipKey = `leadership${countryCode.toUpperCase()}`;

  return (
    <Box sx={styles.mainContainer}>
      <Box sx={styles.wrapper}>
        <Box sx={styles.contentContainer}>
          <Box sx={styles.textSection}>
            <Typography variant="h1_gothic" sx={styles.heading}>
              {t(`${leadershipKey}.heading`)}
            </Typography>
            <Typography variant="h5" sx={styles.subHeading}>
              {t(`${leadershipKey}.subHeading`)}
            </Typography>
            <Typography sx={styles.description}>
              {t(`${leadershipKey}.description`)}
            </Typography>
            <Button sx={styles.button}>{t(`${leadershipKey}.buttonText`)}</Button>
          </Box>

          <Box sx={styles.videoSection}>
            <Box sx={styles.videoContainer}>
              <Box
                component="iframe"
                src={config.videoUrl}
                title={t(`${leadershipKey}.videoTitle`)}
                sx={styles.iframe}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SomaliaLeadership;